
export type Language = 'en' | 'pt' | 'es' | 'fr';

export interface Scene {
  id: string;
  slug: string;
  status: 'draft' | 'complete' | 'warning' | 'review';
  page: number;
}

export interface PromptItem {
  id: string;
  title: string;
  content: string;
  tags: string[];
}

export enum TabOption {
  ELEMENTS = 'Elements',
  ACTIONS = 'Actions',
  PROMPTS = 'Prompts',
  CONFIG = 'Config'
}

export type BlockType = 'scene-heading' | 'action' | 'character' | 'dialogue' | 'parenthetical' | 'transition' | 'sfx' | 'camera-direction';

export interface CameraSuggestion {
  scale: string;
  angle: string;
  move: string;
  focus?: string;
  justification: string;
  cueText: string;
}

export interface SceneCameraDirection {
  sceneNumber: number | string;
  slug: string;
  intention: string;
  suggestions: CameraSuggestion[];
}

export interface AnalysisResult {
  task: string;
  type?: string;
  content: string;
  debugLog?: string;
  round?: number;
  previousContext?: string;
  projectId?: string;
  versionId?: string;
  isRestored?: boolean;
  startLine?: number;
  startLineLogical?: number;
  /** Accumulated list of issues that were already corrected in previous rounds */
  fixedIssues?: { original: string; suggestion: string; rule: string }[];
}

export interface DurationEstimate {
  estimatedMinutes: number;
  sceneBreakdown: {
    sceneSlug: string;
    slug?: string;
    estimatedMinutes: number;
    estimatedTime: string;
    estimatedShots?: number;
    pacing?: string;
    reasoning?: string;
    type?: string;
  }[];
  reasoning: string;
}

export type ElementCategory = 'CHARACTER' | 'LOCATION' | 'PROP' | 'SFX_VFX' | 'CAMERA' | 'TRANSITION' | string;

export interface EffectSuggestion {
  type: 'SFX' | 'VFX' | 'EFFECTS';
  name: string;
  why: string;
  evidence: string;
  intensity: 'low' | 'medium' | 'high';
  confidence: number;
}

export interface SceneEffectsResult {
  sceneId: string;
  sceneHeading: string;
  suggestions: EffectSuggestion[];
}

export type StageMode = 'landing' | 'import' | 'chat' | 'editor';

export interface CameraMeta {
  scale?: string;
  angle?: string;
  type?: string;
  move?: string;
  focus?: string;
  rawCue?: string;
  langHint?: 'en' | 'pt' | 'es' | 'fr';
}

export interface ScriptBlock {
  id: string;
  type: BlockType;
  content: string;
  metadata?: boolean;
  align?: 'left' | 'center' | 'right';
  camera?: CameraMeta;
  isAiSuggestion?: boolean;
}

// Phase 3: Analysis Types
export interface SceneItem {
  id: string; // block id
  slug: string;
  page: number;
  isSlowPaced?: boolean; // Structural warning
}

export interface CharacterStat {
  name: string;
  dialogueCount: number;
  sceneCount: number; // For tooltip
  description?: string;
  dialogueBlockIds: string[]; // For navigation anchors
  isNarrator?: boolean;
  isOffScreen?: boolean;
  isIntroduced?: boolean;
  isInferred?: boolean;
}

export interface CharacterProfile {
  id: string; // Persistent ID for the character
  name: string;
  role: 'Protagonista' | 'Antagonista' | 'Coadjuvante' | 'Figurante' | 'Entrevistado' | 'Narrador' | 'Mascote' | string;
  avatarUrl?: string;
  isModified?: boolean;
  data: {
    identity: {
      full_name: string;
      age_range: string;
      profession_or_role: string;
      social_class: string;
      era_setting: string;
      current_location: string;
      is_real_person: boolean;
      is_inferred: boolean;
    };
    psychology: {
      archetype: string;
      personality_traits: string[];
      moral_code_or_stance: string | null;
      core_fears_or_pains: string | null;
      primary_goal: string;
    };
    expression: {
      speech_style: string;
      conversation_topics: string[];
      sense_of_humor: string;
      visual_description: string;
    };
  };
}


export interface LocationStat {
  name: string;
  count: number;
  description?: string;
  sceneBlockIds: string[]; // For navigation anchors
}

export interface PropStat {
  name: string;
  count: number;
  description?: string;
  sceneBlockIds: string[]; // For navigation anchors
}

export interface SfxStat {
  name: string;
  count: number;
  description?: string;
  sceneBlockIds: string[];
}

export interface CameraStat {
  name: string;
  count: number;
  description?: string;
  sceneBlockIds: string[];
}

export interface TransitionStat {
  name: string;
  count: number;
  description?: string;
  sceneBlockIds: string[];
}

export interface AnalysisData {
  scenes: SceneItem[];
  characters: CharacterStat[];
  locations: LocationStat[];
  props: PropStat[]; // Added Props
  sfx: SfxStat[];
  cameras: CameraStat[];
  transitions: TransitionStat[];
  customCategories?: Record<string, any[]>; // For dynamically created categories
  totalPages: number;
  estimatedPdfPages: number;
  totalWords: number;
}

// Phase 1.9: Educational Context Types
export interface ProTipAction {
  label: string;
  description: string;
  actionPayload: string;
}

export interface EducationalData {
  technique: string;
  explanation: string;
  examples: string[];
  proTip: ProTipAction;
}

// Phase 1.8: Ideation Chat
export interface ChatMessage {
  id: string;
  role: 'user' | 'ai';
  content: string;
  educationalData?: EducationalData; // Added optional educational layer
  briefingData?: {
    questionId: string;
    label: string;
    type: 'text' | 'textarea';
    required?: boolean;
  };
  timestamp: number;
}

// Phase 5.1: Multi-Version Scripting
export interface ScriptVersion {
  id: string;
  name: string;
  blocks: ScriptBlock[];
  language?: Language;
  createdAt: number;
  infoMetadata?: string; // Stores AI analysis/classification/pillars from generation
  auditResult?: AnalysisResult;
  originChatId?: string;
  originMessageId?: string;
  sessionId?: string;
  chatTouched?: boolean;
}

// Phase 5: Project Management Types
export interface Project {
  id: string;
  name: string;
  author?: string; // Added Author field
  basedOn?: string;
  contactInfo?: string;
  status?: 'active' | 'archived'; // Add status for archive/restore
  createdAt?: number; // Creation timestamp
  genre?: string;
  description?: string;
  // Legacy support: 'blocks' might be deprecated in future, but kept for safe migration
  blocks: ScriptBlock[];
  scriptVersions: ScriptVersion[]; // New: List of generated scripts
  activeVersionId?: string; // Pointer to currently selected version
  chatHistory?: ChatMessage[];
  characterProfiles?: Record<string, CharacterProfile>; // Map Character Name -> Profile
  locationProfiles?: Record<string, string>; // Map Location Name -> Description
  propProfiles?: Record<string, string>; // Map Prop Name -> Description
  sfxProfiles?: Record<string, string>; // Map SFX Name -> Description
  cameraProfiles?: Record<string, string>; // Map Camera Name -> Description
  transitionProfiles?: Record<string, string>; // Map Transition Name -> Description
  lastModified: number;

  // New Pillar Data Persistence
  detectedFormat?: string;
  pillarsData?: Record<string, string>;
  manualOverrides?: Record<string, ElementCategory>; // Map Element Name -> Category

  // Phase 7: Audit Persistence
  verifiedSegments?: string[]; // Array of exact paragraph content that has been audited/corrected

  // AI Formats & Briefings
  formatId?: string; // e.g. "ai_film", "ai_doc"
  renderMode?: 'master_scene' | 'dual_column';
  briefingAnswers?: Record<string, string>;
}

export interface CustomMethod {
  id: string;
  name: string;
  content: string;
  isActive: boolean;
  signature?: string; // Content-based fingerprint to avoid duplicates
}

// Phase 6: Production Prompts
export interface PromptSet {
  midjourney: string;
  nano: string;
  kling: string;
  veo: string;
}

export interface ShotItem {
  id: number;
  shotType: string; // e.g., "Medium Shot", "Close Up"
  visualAction: string; // Brief description
  prompts: PromptSet;
}

export interface SceneShotList {
  sceneNumber?: number;
  slug?: string;
  shots?: ShotItem[];
}

// New: 27 Pillars System
export type ScriptFormatType = 'Film' | 'Animation' | 'Documentary' | 'Advertisement' | 'TV' | 'All';

export interface PillarDefinition {
  id: string;
  number: number;
  name: string;
  description: string;
  recommendedFormats: ScriptFormatType[];
}

export interface AppConfig {
  structuralAnalysis: boolean;
  visualAtmosphere: boolean;
  characterArcs: boolean;
  plantAndPayoff: boolean; // New Feature
  methodMcKee: boolean;
  methodField: boolean;
  panWithSpace: boolean; // New: Pan toggle
  linePagination: boolean;

  language: Language;
  customMethods: CustomMethod[]; // New: Custom User Methods
  creativityTemperature?: number; // New: Creativity slider for Paste/Import
  scriptMode?: 'spec' | 'shooting'; // New: Technical Standard Mode (Default: 'spec')
  matureLanguage?: boolean; // New: Allow Profanity/Mature Content
  enableHorrorOps?: boolean; // New: Enable Horror/Violent Content Mode
  scriptFormat?: ScriptFormatType; // New: Global format setting
}

export interface AppState {
  projects: Project[];
  activeProjectId: string;
  activeTab: TabOption;
  activeAIModal: string | null; // Persisted AI Modal State
  activeAnalysisTask: AnalysisResult | null; // Persisted Analysis Result
  analysis: AnalysisData;
  config: AppConfig;
  activeFormatId?: string; // Currently selected category for new projects
  activeBriefingAnswers?: Record<string, string>; // Answers for the current briefing session
}

export interface ReviewProgressState {
  analyzedScenesCount: number;
  totalScenes: number;
  currentProcessingHeading: string;
  statusMessage?: string;
}
